import { motion } from "framer-motion";
import { Globe, Sprout, Ship, ShieldCheck, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../i18n";
import { MeshBackground } from "./MeshBackground";

const ICONS = [Globe, Sprout, Ship, ShieldCheck];
const TESTIDS = ["service-card-export", "service-card-production", "service-card-logistics", "service-card-quality"];
const SPANS = ["sm:col-span-2 lg:col-span-4", "lg:col-span-2", "lg:col-span-2", "sm:col-span-2 lg:col-span-4"];

export const ServicesGrid = () => {
  const { t } = useLanguage();

  return (
    <section id="faaliyetler" data-testid="services-section" className="relative bg-[#f4faf7] py-24 sm:py-32">
      <MeshBackground />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-[#1d8a52]"
          >
            {t.services.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="font-display mt-4 text-2xl font-bold leading-tight tracking-tight text-[#062316] sm:text-3xl lg:text-4xl"
            data-testid="services-title"
          >
            {t.services.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base"
          >
            {t.services.sub}
          </motion.p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-6">
          {t.services.items.map((item, i) => {
            const Icon = ICONS[i];
            return (
              <motion.article
                key={item.title}
                data-testid={TESTIDS[i]}
                data-cursor
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                className={`gradient-border group relative flex flex-col justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white/90 p-8 text-center backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:border-transparent hover:shadow-[0_30px_80px_-24px_rgba(72,227,142,0.45),inset_0_0_50px_rgba(72,227,142,0.08)] ${SPANS[i]}`}
              >
                <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#48e38e]/10 blur-2xl transition-all duration-500 group-hover:bg-[#48e38e]/25" />
                <div className="relative">
                  <ArrowUpRight
                    size={20}
                    className="absolute right-0 top-0 text-slate-300 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#1d8a52]"
                  />
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-[#062316] text-[#48e38e] transition-all duration-500 group-hover:rotate-3 group-hover:scale-110 group-hover:bg-[#48e38e] group-hover:text-[#062316]">
                    <Icon size={26} strokeWidth={1.75} />
                  </span>
                  <span lang="en" className="mt-7 inline-block rounded-full border border-[#48e38e]/40 bg-[#e9faf1] px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#14663d]">
                    {item.tag}
                  </span>
                  <h3 className="font-display mt-4 text-xl font-semibold text-[#062316] sm:text-2xl">{item.title}</h3>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">{item.desc}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
